import { useAtomValue } from "jotai";
import { type FunctionComponent, type ReactNode, type Ref, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { rosettaAtom } from "scmp-app/lib/rosetta";
import type { topStoriesHomeV2QueueItemsEdge$key } from "scmp-app/queries/__generated__/topStoriesHomeV2QueueItemsEdge.graphql";

import { sendTopStoriesClickTracking, useTopStoriesNativeAdImpressionTracking } from "./hooks";
import {
  AdSlotContainer,
  Container,
  HorizontalDivider,
  InlineAds,
  PlusWidget,
  StyledTopStoriesItem,
  TopStory1,
  TopStory10,
  TopStory11,
  TopStory12,
  TopStory13,
  TopStory14,
  TopStory15,
  TopStory2,
  TopStory3,
  TopStory4,
  TopStory5,
  TopStory6,
  TopStory7,
  TopStory8,
  TopStory9,
  VerticalDivider,
} from "./styles";

const topStoriesArticleCount = 15;

type Props = {
  className?: string;
  getImpressionTrackingFunction: (index: number) => Ref<HTMLDivElement> | undefined;
  reference: topStoriesHomeV2QueueItemsEdge$key;
  slots?: {
    mobileInlineAds?: ReactNode;
    nativeAds?: ReactNode;
    plusWidget?: ReactNode;
    sponsorArticle?: (
      tracking: typeof sendTopStoriesClickTracking,
      ref?: Ref<HTMLDivElement>,
    ) => ReactNode;
  };
};

export const HomeV2TopStories: FunctionComponent<Props> = ({
  className,
  getImpressionTrackingFunction,
  reference,
  slots = {},
}) => {
  const edges = useFragment(
    graphql`
      fragment topStoriesHomeV2QueueItemsEdge on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Content {
            entityId
            ...homeSecondaryV2ContentItemContent
            ...topStoriesItemV2Content
          }
        }
      }
    `,
    reference,
  );

  const contents = (edges ?? [])
    .map(edge => edge?.node)
    /**
     * QueueItem is a union of 17 types, but only 6 implement the Content
     * interface (Article, Gallery, Page, Podcast, Recipe, Video). Since
     * entityId is selected via `... on Content`, non-Content types (e.g.
     * Author, Section, Topic) don't match the inline fragment and their
     * entityId is undefined. Filter them out so only Content items reach
     * HomeV2TopStoriesItem.
     */
    .filter(node => notEmpty(node) && notEmpty(node.entityId))
    .slice(0, topStoriesArticleCount);

  const captureFirstNativeAdImpressionEvent = useTopStoriesNativeAdImpressionTracking();

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false;

  const sponsorArticle = useMemo(
    () => slots?.sponsorArticle?.(sendTopStoriesClickTracking, captureFirstNativeAdImpressionEvent),
    [captureFirstNativeAdImpressionEvent, slots],
  );

  const topStoryComponents = [
    TopStory1,
    TopStory2,
    TopStory3,
    TopStory4,
    TopStory5,
    TopStory6,
    TopStory7,
    TopStory8,
    TopStory9,
    TopStory10,
    TopStory11,
    TopStory12,
    TopStory13,
    TopStory14,
    TopStory15,
  ];

  if (!notEmpty(contents)) return null;
  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.TopStory,
      }}
    >
      <Container $hasScmpPlusAccessRight={hasScmpPlusAccessRight} className={className}>
        {topStoryComponents.map((Component, index) => (
          <Component key={index}>
            {contents[index] && (
              <StyledTopStoriesItem
                isShowImage
                onClick={(entityId, urlAlias, isSponsorArticle) => {
                  sendTopStoriesClickTracking({
                    entityId,
                    index,
                    isSponsorArticle,
                    urlAlias,
                  });
                }}
                ref={getImpressionTrackingFunction(index)}
                reference={contents[index]}
                withCarousel
                withExclusiveHeaderFlag
                withSeriesShortLabel
                withSummary
              />
            )}
            {index === 3 && sponsorArticle && (
              <>
                <VerticalDivider />
                <HorizontalDivider />
                {sponsorArticle}
              </>
            )}
            {index === 14 && slots.nativeAds && (
              <>
                <VerticalDivider />
                <HorizontalDivider />
                <AdSlotContainer>{slots.nativeAds}</AdSlotContainer>
              </>
            )}
            {index === 7 && <VerticalDivider />}
          </Component>
        ))}

        {slots.mobileInlineAds && <InlineAds>{slots.mobileInlineAds}</InlineAds>}
        {slots.plusWidget && hasScmpPlusAccessRight && <PlusWidget>{slots.plusWidget}</PlusWidget>}
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2TopStories.displayName = "HomeV2TopStories";
